import { formatEuros, nightsBetween, plainDate, verhuurtypeVoor } from "@bivak/domain";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { MonthGrid } from "../components/kalender/MonthGrid.js";
import { TentMark } from "../components/TentMark.js";
import { nl } from "../copy/nl.js";
import {
  datumsGeldig,
  gridMaandOffset,
  ontbrekendeVelden,
  periodeBezet,
} from "../lib/aanvraagForm.js";
import {
  ApiError,
  createBooking,
  fetchAvailability,
  fetchQuote,
  type NewBooking,
} from "../lib/api.js";
import { monthStart } from "../lib/calendar.js";
import { asCents, formatDateRange } from "../lib/format.js";
import { volgendeSelectie } from "../lib/selectie.js";
import { uuid } from "../lib/uuid.js";

// The public shell is a pathless layout with id "public", so the route id
// carries that prefix even though the URL is plain /aanvraag.
const route = getRouteApi("/public/aanvraag");

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function Veld({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: the input arrives via children — the label wraps its control, biome just cannot see through the prop.
    <label className="block">
      <span className="mb-1.5 block font-semibold text-xs">{label}</span>
      {children}
      {hint && <span className="mt-1.5 block text-[11.5px] text-mist leading-snug">{hint}</span>}
    </label>
  );
}

const INPUT =
  "min-h-11 w-full rounded-lg border-2 border-lijn bg-kaart px-3 text-sm outline-none focus:border-dennen";

/** Shows the grid's choice; tapping it goes to the grid instead of opening a
 *  second (availability-blind) native picker. Needs its own aria-label: a
 *  wrapping <label> names an input but never a button (HTML-AAM), so without
 *  it both fields announce identically to a screen reader (review find). */
function DatumVeld({
  label,
  waarde,
  onClick,
}: {
  label: string;
  waarde: string;
  onClick: () => void;
}) {
  const gekozen = ISO.test(waarde);
  const tekst = gekozen ? formatDateRange(waarde, waarde) : nl.aanvraag.velden.kiesInKalender;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}: ${tekst}`}
      className={`${INPUT} cursor-pointer text-left ${gekozen ? "" : "text-mist"}`}
    >
      {tekst}
    </button>
  );
}

/**
 * SCHERM 3 in the sketch, now real: the request form with the live itemised
 * price. The server re-quotes on submit — the price shown here is display-only.
 */
export function AanvraagPage() {
  const search = route.useSearch();

  // One id per attempt, generated up front: retries and double clicks reuse it,
  // so the server can dedupe on its primary key (rules/api.md idempotency).
  const [boekingId, setBoekingId] = useState(() => uuid());
  const [van, setVan] = useState(search.van ?? "");
  const [tot, setTot] = useState(search.tot ?? "");
  // Empty, not a silent default: a prefilled 20 produced a confident price
  // for a group that was never 20 (persona review). The quote appears once
  // the renter has said who they are.
  const [personen, setPersonen] = useState("");
  const [groepsnaam, setGroepsnaam] = useState("");
  const [contactNaam, setContactNaam] = useState("");
  const [email, setEmail] = useState("");
  const [telefoon, setTelefoon] = useState("");
  const [doel, setDoel] = useState("");
  // Kampas takeaways (0.22): groups always have one odd question, and their
  // treasurer often wants a reference of their own on the paperwork.
  const [opmerking, setOpmerking] = useState("");
  const [referentie, setReferentie] = useState("");

  const aantal = Number.parseInt(personen, 10);
  const datesValid = datumsGeldig(van, tot);
  // Grid-only values are always real dates; the seasons rule decides the name.
  const formule = datesValid ? verhuurtypeVoor(plainDate(van), plainDate(tot)) : null;
  const aantalGeldig = Number.isInteger(aantal) && aantal >= 1 && aantal <= 80;
  // A dag prices flat (€100/day) and books without any headcount at all —
  // the field is not even rendered for it (user calls, 2026-08-21/22).
  const isDag = formule?.ok === true && formule.verhuurtype === "dag";
  // Overnight stays price per person, so only they ask for the estimate; the
  // under-31 split is not asked at all — settled from actuals (user call,
  // 2026-08-22, and §7: statistics build from actual rows).
  const overnacht = datesValid && tot > van;
  const quoteEnabled = datesValid && (aantalGeldig || isDag);
  const quotePersonen = aantalGeldig ? aantal : 1;

  // The reasons the quote cannot even be asked — silence here is a dead end.
  const invoerFout =
    ISO.test(van) && ISO.test(tot) && van > tot
      ? nl.aanvraag.fouten.omgekeerd
      : Number.isInteger(aantal) && aantal > 80
        ? nl.aanvraag.fouten.teVeel
        : undefined;

  // The same availability the calendar page shows, embedded under the date
  // fields: occupied days are visibly taken BEFORE the renter picks them,
  // instead of a surprise later (user feedback, after Kampas' struck-through
  // picker — ours also says WHY a day is unavailable).
  const beschikbaarheid = useQuery({
    queryKey: ["availability"],
    queryFn: () => fetchAvailability(),
    staleTime: 60_000,
  });
  // Grid month and occupancy pre-check both live in lib/aanvraagForm.ts,
  // where boundary values meet unit tests (user call: this is schoolbook
  // unit-testing territory, not component territory).
  const [handmatigeMaand, setHandmatigeMaand] = useState<number | null>(null);
  const maandOffset = beschikbaarheid.data
    ? gridMaandOffset(handmatigeMaand, beschikbaarheid.data.from, van, 12)
    : 0;

  const gridRef = useRef<HTMLDivElement>(null);
  const naarKalender = () =>
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const bezetFout = periodeBezet(van, tot, beschikbaarheid.data?.spaces[0]?.unavailable ?? [])
    ? nl.aanvraag.fouten.bezet
    : undefined;

  const quote = useQuery({
    queryKey: ["quote", van, tot, quotePersonen],
    queryFn: () => fetchQuote(van, tot, quotePersonen),
    enabled: quoteEnabled,
    retry: (count, err) => (err instanceof ApiError && err.status < 500 ? false : count < 1),
    staleTime: 60_000,
  });

  const verzenden = useMutation({
    mutationFn: (body: NewBooking) => createBooking(body),
  });

  if (verzenden.isSuccess) {
    return (
      <div className="mx-auto max-w-xl py-10 text-center">
        <div className="mb-4 flex justify-center">
          <TentMark className="size-12" />
        </div>
        <h1 className="font-bold font-display text-3xl tracking-tight">
          {nl.aanvraag.succes.titel}
        </h1>
        <p className="mt-3 text-mist">{nl.aanvraag.succes.tekst}</p>
        {verzenden.data.totalCents !== undefined && (
          <p className="mt-4 font-bold font-mono text-lg">
            {nl.aanvraag.prijs.totaal}: {formatEuros(asCents(verzenden.data.totalCents))}
          </p>
        )}
        <button
          type="button"
          onClick={() => {
            verzenden.reset();
            setBoekingId(uuid());
          }}
          className="mt-6 min-h-11 rounded-xl border-2 border-lijn px-5 font-semibold text-sm"
        >
          {nl.aanvraag.succes.nieuwe}
        </button>
      </div>
    );
  }

  const klaar =
    quoteEnabled &&
    quote.isSuccess &&
    bezetFout === undefined &&
    (overnacht ? aantalGeldig : true) &&
    groepsnaam.trim().length >= 2 &&
    contactNaam.trim().length >= 2 &&
    email.includes("@") &&
    telefoon.trim().length >= 6;

  // Everything still standing between the renter and the submit, by name.
  const ontbreekt = ontbrekendeVelden(
    {
      van,
      tot,
      personen,
      groepsnaam,
      contactNaam,
      email,
      telefoon,
    },
    overnacht,
  ).map((veld) => nl.aanvraag.veldnamen[veld]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!klaar || verzenden.isPending) return;
    verzenden.mutate({
      boekingId,
      van,
      tot,
      // A dag books without a headcount; the estimate travels only when asked.
      ...(overnacht ? { personen: aantal } : {}),
      groepsnaam: groepsnaam.trim(),
      contactNaam: contactNaam.trim(),
      email: email.trim(),
      telefoon: telefoon.trim(),
      ...(doel.trim() === "" ? {} : { doel: doel.trim() }),
      ...(opmerking.trim() === "" ? {} : { opmerking: opmerking.trim() }),
      ...(referentie.trim() === "" ? {} : { referentie: referentie.trim() }),
    });
  };

  return (
    <div>
      <h1 className="squiggle font-bold font-display text-3xl tracking-tight">
        {nl.aanvraag.titel}
      </h1>
      {/* DERIVED from the dates, never from the URL: a ?type=kamp link whose
          dates were then changed to an October day kept saying "Kampverhuur"
          (user find, 2026-08-21). The domain rule is the single source. */}
      {formule?.ok === true && (
        <p className="mt-4 inline-block rounded-lg bg-zand px-4 py-2 text-sm">
          <span className="font-semibold">{nl.aanvraag.gekozenType}:</span>{" "}
          {nl.kalender.types[formule.verhuurtype].naam}
        </p>
      )}

      <form
        onSubmit={submit}
        className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-[minmax(0,1fr)_320px]"
      >
        <div className="flex max-w-xl flex-col gap-4">
          {/* Display fields, not <input type="date">: on a phone the native
              date picker opened OVER our availability grid — a second calendar
              that cannot show bezette dagen, the exact Kampas failure the grid
              exists to avoid (user find on-device, 2026-08-21). The grid below
              is the only picker; these show its result and scroll to it. */}
          <div className="grid grid-cols-2 gap-4">
            <Veld label={nl.aanvraag.velden.aankomst}>
              <DatumVeld label={nl.aanvraag.velden.aankomst} waarde={van} onClick={naarKalender} />
            </Veld>
            <Veld label={nl.aanvraag.velden.vertrek}>
              <DatumVeld label={nl.aanvraag.velden.vertrek} waarde={tot} onClick={naarKalender} />
            </Veld>
          </div>
          {beschikbaarheid.data && beschikbaarheid.data.spaces[0] && (
            <div ref={gridRef} className="scroll-mt-4">
              {/* Mid-selection guidance, same voice as the public calendar:
                  one date in, the next step is named. The ‹ › live in the
                  month's own header (MonthGrid nav), same as the kalender. */}
              <p
                className={`mb-1 ${
                  ISO.test(van) && !ISO.test(tot)
                    ? "font-semibold text-[11.5px] text-rood"
                    : "text-[11.5px] text-mist"
                }`}
              >
                {ISO.test(van) && !ISO.test(tot)
                  ? nl.kalender.selectie.kiesVertrek
                  : nl.aanvraag.kalenderHint}
              </p>
              <MonthGrid
                nav={{
                  vorige: () => setHandmatigeMaand(maandOffset - 1),
                  volgende: () => setHandmatigeMaand(maandOffset + 1),
                  vorigeUit: maandOffset === 0,
                  volgendeUit: maandOffset >= 11,
                }}
                month={monthStart(beschikbaarheid.data.from, maandOffset)}
                space={beschikbaarheid.data.spaces[0]}
                occupancy={beschikbaarheid.data.occupancy}
                todayIso={beschikbaarheid.data.from}
                isSelected={(dagIso) => {
                  if (!ISO.test(van)) return false;
                  const einde = ISO.test(tot) && tot >= van ? tot : van;
                  return dagIso >= van && dagIso <= einde;
                }}
                onSelectDay={(dagIso) => {
                  const selectie = volgendeSelectie(
                    {
                      ...(ISO.test(van) ? { van } : {}),
                      ...(ISO.test(tot) ? { tot } : {}),
                    },
                    dagIso,
                  );
                  setVan(selectie.van ?? "");
                  setTot(selectie.tot ?? "");
                }}
              />
            </div>
          )}
          {/* Only overnight stays ask a headcount (per-person price); a dag is
              flat. The under-31 question is gone from the form entirely — it
              is settled from actuals at the afrekening (user, 2026-08-22). */}
          {overnacht && (
            <div className="grid grid-cols-2 gap-4">
              <Veld label={nl.aanvraag.velden.personen}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={80}
                  value={personen}
                  onChange={(e) => setPersonen(e.target.value)}
                  required
                  className={INPUT}
                />
              </Veld>
            </div>
          )}
          <Veld label={nl.aanvraag.velden.groepsnaam}>
            <input
              value={groepsnaam}
              onChange={(e) => setGroepsnaam(e.target.value)}
              required
              className={INPUT}
            />
          </Veld>
          <Veld label={nl.aanvraag.velden.doel}>
            <input value={doel} onChange={(e) => setDoel(e.target.value)} className={INPUT} />
          </Veld>
          <div className="grid grid-cols-2 gap-4">
            <Veld label={nl.aanvraag.velden.contactNaam}>
              <input
                value={contactNaam}
                onChange={(e) => setContactNaam(e.target.value)}
                required
                className={INPUT}
              />
            </Veld>
            <Veld label={nl.aanvraag.velden.telefoon}>
              <input
                type="tel"
                value={telefoon}
                onChange={(e) => setTelefoon(e.target.value)}
                required
                className={INPUT}
              />
            </Veld>
          </div>
          <Veld label={nl.aanvraag.velden.email} hint={nl.aanvraag.velden.emailHint}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={INPUT}
            />
          </Veld>
          <Veld label={nl.aanvraag.velden.referentie} hint={nl.aanvraag.velden.referentieHint}>
            <input
              value={referentie}
              maxLength={60}
              onChange={(e) => setReferentie(e.target.value)}
              className={INPUT}
            />
          </Veld>
          <Veld label={nl.aanvraag.velden.opmerking} hint={nl.aanvraag.velden.opmerkingHint}>
            <textarea
              value={opmerking}
              maxLength={500}
              rows={3}
              onChange={(e) => setOpmerking(e.target.value)}
              className={`${INPUT} py-2.5 leading-snug`}
            />
          </Veld>
        </div>

        <aside className="h-fit rounded-2xl bg-dennen-diep p-5 text-[#e9efe8] md:sticky md:top-4">
          <h2 className="font-bold font-display text-base text-kaart">{nl.aanvraag.prijs.titel}</h2>
          <p className="mb-3 text-[#a9bca9] text-[11.5px]">{nl.aanvraag.prijs.sub}</p>
          {quote.isSuccess ? (
            <>
              {quote.data.lines.map((line) => (
                <div
                  key={line.kind}
                  className="flex items-baseline justify-between gap-3 border-[#46604c] border-b border-dashed py-1.5 text-[13px]"
                >
                  <span>
                    {nl.aanvraag.prijs.lijn[line.kind] ?? line.kind}
                    <span className="block text-[#a9bca9] text-[11px]">
                      {line.kind === "overnight_persons" && datesValid
                        ? nl.aanvraag.prijs.nachtenMaalPersonen(
                            nightsBetween(plainDate(van), plainDate(tot)),
                            aantal,
                          )
                        : `${line.quantity} ×`}{" "}
                      {formatEuros(asCents(line.unitCents))}
                    </span>
                  </span>
                  <span className="font-mono">{formatEuros(asCents(line.amountCents))}</span>
                </div>
              ))}
              <div className="flex items-baseline justify-between pt-3 pb-4 font-bold">
                <span>{nl.aanvraag.prijs.totaal}</span>
                <span className="font-mono text-kaart text-xl">
                  {formatEuros(asCents(quote.data.totalCents))}
                </span>
              </div>
            </>
          ) : (
            <p className="pb-4 text-[#a9bca9] text-[13px]">
              {invoerFout ??
                bezetFout ??
                (quote.error instanceof ApiError ? quote.error.message : "…")}
            </p>
          )}
          <button
            type="submit"
            disabled={!klaar || verzenden.isPending}
            className="min-h-11 w-full rounded-xl bg-rood font-semibold text-kaart text-sm hover:bg-rood-donker disabled:opacity-50"
          >
            {verzenden.isPending ? nl.aanvraag.bezig : nl.aanvraag.verstuur}
          </button>
          {!klaar && !verzenden.isPending && (ontbreekt.length > 0 || bezetFout) && (
            <p className="mt-3 font-semibold text-[#f3c1b3] text-[12px] leading-relaxed">
              {bezetFout ?? nl.aanvraag.nogNodig(ontbreekt.join(", "))}
            </p>
          )}
          {verzenden.error instanceof ApiError && (
            <p className="mt-3 font-semibold text-[#f3c1b3] text-[12.5px]">
              {verzenden.error.message}
            </p>
          )}
          <p className="mt-3 text-[#a9bca9] text-[11px] leading-relaxed">
            {nl.aanvraag.vrijblijvend} {nl.aanvraag.antwoordBelofte}
          </p>
        </aside>
      </form>
    </div>
  );
}
